import { call } from '@advanza/api'
import { SelectSimple } from '@advanza/input'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { CHURN_REASON_DISPLAY_MAP } from 'components/reports/common/constants'
import { useState } from 'react'

interface ChurnReasonEditorProps {
    premiumCancelRequestId: number
    currentReason: number | null
    providerId: number
    onSave?: () => void
}

const ChurnReasonEditor = NiceModal.create(
    ({
        premiumCancelRequestId,
        currentReason,
        providerId: _providerId,
        onSave,
    }: ChurnReasonEditorProps) => {
        const modal = useModal()
        const [selectedReason, setSelectedReason] = useState<string>(
            currentReason?.toString() || '',
        )
        const [isSaving, setIsSaving] = useState(false)
        const [error, setError] = useState<string | null>(null)

        const reasonOptions = Object.keys(CHURN_REASON_DISPLAY_MAP)
            .sort((a, b) => {
                const numA = parseInt(a, 10)
                const numB = parseInt(b, 10)
                return numA - numB
            })
            .map((key) => ({
                value: key,
                name: CHURN_REASON_DISPLAY_MAP[key],
            }))

        const handleSave = async () => {
            if (!selectedReason) {
                setError('Please select a churn reason')
                return
            }

            setIsSaving(true)
            setError(null)

            try {
                await call('office/cancellations/update-reason', {
                    method: 'post',
                    body: JSON.stringify({
                        premium_cancel_request_id: premiumCancelRequestId,
                        reason: parseInt(selectedReason, 10),
                    }),
                })

                if (onSave) {
                    onSave()
                }
                modal.hide()
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Failed to update churn reason')
            } finally {
                setIsSaving(false)
            }
        }

        const handleCancel = () => {
            modal.hide()
        }

        return (
            <BaseModal onClose={handleCancel}>
                <BaseModalHeader>
                    <h2>Edit Churn Reason</h2>
                </BaseModalHeader>
                <BaseModalMainContent>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-md font-medium font-bold text-neutral-700 mb-1">
                                Churn Reason *
                            </label>
                            <SelectSimple
                                value={selectedReason}
                                onChange={(e) => {
                                    setSelectedReason(String(e.target.value || ''))
                                    setError(null)
                                }}
                                options={reasonOptions}
                                disabled={isSaving}
                            />
                        </div>
                        {error && (
                            <div className="text-md text-red-600 bg-red-50 p-2 rounded">
                                {error}
                            </div>
                        )}
                    </div>
                </BaseModalMainContent>
                <BaseModalFooter>
                    <div className="flex justify-end gap-2">
                        <Button
                            appearance="outlined_primary"
                            onClick={handleCancel}
                            disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button appearance="primary" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default ChurnReasonEditor
